import { Children, type FC, type ReactNode } from 'react';
import { QBox, QFlex, QSpacer, QStack } from '../../QLayouts';
import { QDivider } from '../../QAtoms';
import { usePartitionedChildren } from '../../utils/partitionChildren';

export type QHeaderProps = {
  showDivider?: boolean;
  children?: ReactNode;
};

export const QHeader: FC<QHeaderProps> = ({ children, showDivider = true }) => {
  const {
    QBackLink,
    QTabs,
    QTitle,
    QSubtitle,
    QButtonGroup,
    QHorizontalMetadata,
    QAlert,
    QAlertBox,
    QApprovalRequestAlert,
    QCloseButton,
    unmatched,
  } = usePartitionedChildren(
    children,
    'QBackLink',
    'QTabs',
    'QTitle',
    'QSubtitle',
    'QButtonGroup',
    'QHorizontalMetadata',
    'QAlert',
    'QAlertBox',
    'QApprovalRequestAlert',
    'QCloseButton',
  );

  return (
    <QStack direction="column" alignSelf="stretch" flexShrink={1} spacing="8px">
      {QBackLink.length && <QBox>{QBackLink}</QBox>}
      <TopRow>{QTabs}</TopRow>
      <QStack spacing="8px">
        <MiddleRow>
          {QTitle}
          {(QButtonGroup.length > 0 || QCloseButton.length > 0) && (
            <>
              <QSpacer />
              <QStack direction="row" paddingTop="4px">
                {QButtonGroup}
                {QCloseButton}
              </QStack>
            </>
          )}
        </MiddleRow>
        {QSubtitle}
      </QStack>
      {QHorizontalMetadata}
      {unmatched}
      <AlertRow>
        {QAlert}
        {QAlertBox}
        {QApprovalRequestAlert}
      </AlertRow>
      {showDivider && <QDivider paddingTop="16px" />}
    </QStack>
  );
};

QHeader.displayName = 'QHeader';

const TopRow: FC<{ children?: ReactNode }> = ({ children }) =>
  Children.count(children) > 0 ? (
    <QBox paddingBottom="8px">{children}</QBox>
  ) : null;

const MiddleRow: FC<{ children?: ReactNode }> = ({ children }) =>
  Children.count(children) > 0 ? (
    <QFlex direction="row">{children}</QFlex>
  ) : null;

const AlertRow: FC<{ children?: ReactNode }> = ({ children }) =>
  Children.count(children) > 0 ? (
    <QStack spacing="8px" paddingTop="8px">
      {children}
    </QStack>
  ) : null;
